import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Heart, ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * /family/lookbook/:studentId
 * Parent-only, read-only view of a linked student's AI try-on lookbook.
 */
export default function ChildLookbook() {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.familyChildLookbook(studentId)
      .then(({ data }) => setData(data))
      .catch((e) => setError(errMsg(e)));
  }, [studentId]);

  if (error) return <div className="max-w-[900px] mx-auto px-6 py-24 text-center"><p className="font-body text-ink/60">{error}</p><Link to="/account" className="mt-6 inline-block font-label text-[10px] tracking-widest text-rosemetal">← BACK TO ACCOUNT</Link></div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;

  return (
    <div data-testid="child-lookbook-page" className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-12 pb-24">
      <Link to="/account" className="inline-flex items-center gap-1.5 font-label text-[10px] tracking-widest text-ink/60 hover:text-rosemetal mb-8"><ArrowLeft className="w-3 h-3" /> BACK</Link>
      <p className="font-label text-[10px] tracking-widest text-rosemetal">STUDENT LOOKBOOK</p>
      <h1 className="font-display text-5xl leading-tight mt-2">{data.student_name}'s try-ons</h1>
      <p className="font-body text-sm text-ink/60 mt-3">Every dress they've tried on with AI — read-only view for parents & guardians.</p>

      {data.sessions.length === 0 ? (
        <p className="font-body text-ink/60 mt-16 text-center">No try-ons yet. Your student can start one from any dress page.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
          {data.sessions.map((s) => (
            <div key={s.id} className="group" data-testid={`lookbook-item-${s.id}`}>
              <div className="aspect-[3/4] bg-blush rounded-sm overflow-hidden">
                {s.result_url ? (
                  <img
                    src={s.result_url.startsWith("http") ? s.result_url : `${process.env.REACT_APP_BACKEND_URL}${s.result_url}`}
                    alt={s.product?.name || "Try-on"}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-label text-[9px] text-ink/40 tracking-widest">NO IMAGE</div>
                )}
              </div>
              {s.product && (
                <Link to={s.product.category_slug && s.product.slug ? `/${s.product.category_slug}/${s.product.slug}` : `/product/${s.product.id}`}
                      className="block mt-2 hover:text-rosemetal">
                  <p className="font-body text-sm text-ink truncate">{s.product.name}</p>
                </Link>
              )}
              <p className="font-label text-[9px] text-ink/40 tracking-widest mt-0.5">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
